import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Where a floating surface hangs from: a trigger it lines up under, or a bare
 * point, which is all a right-click gives us.
 */
export type PopoverAnchor =
  | { readonly kind: 'element'; readonly element: HTMLElement }
  | { readonly kind: 'point'; readonly x: number; readonly y: number };

/** Why the surface went away, because the answer decides where focus lands. */
export type PopoverCloseReason = 'escape' | 'outside' | 'scroll';

export interface PopoverProps {
  anchor: PopoverAnchor;
  onClose: (reason: PopoverCloseReason) => void;
  /** Which edge of the anchor the surface lines up with. */
  align?: 'start' | 'end';
  className?: string;
  children: React.ReactNode;
}

/** The gap under the trigger, and the room kept clear of the window's edges. */
const GAP = 4;
const MARGIN = 8;
const LEAST_USEFUL_HEIGHT = 120;

interface Placement {
  top: number;
  left: number;
  maxHeight: number;
}

interface AnchorBox {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

function boxOf(anchor: PopoverAnchor): AnchorBox {
  if (anchor.kind === 'point') {
    return { top: anchor.y, bottom: anchor.y, left: anchor.x, right: anchor.x };
  }
  const rect = anchor.element.getBoundingClientRect();
  return { top: rect.top, bottom: rect.bottom, left: rect.left, right: rect.right };
}

/**
 * Below the anchor, unless below is the shorter of the two and the content does
 * not fit in it. The height fed in is the content's natural height, never the
 * clamped one, so a surface that has already been cut down to `maxHeight` does
 * not measure shorter and flip itself back on the next pass.
 */
function place(
  anchor: PopoverAnchor,
  natural: { width: number; height: number },
  align: 'start' | 'end',
): Placement {
  const box = boxOf(anchor);
  const viewWidth = window.innerWidth;
  const viewHeight = window.innerHeight;

  const below = viewHeight - box.bottom - GAP - MARGIN;
  const above = box.top - GAP - MARGIN;
  const flip = natural.height > below && above > below;

  const room = Math.max(LEAST_USEFUL_HEIGHT, flip ? above : below);
  const height = Math.min(natural.height, room);
  const top = flip ? Math.max(MARGIN, box.top - GAP - height) : box.bottom + GAP;

  const edge = align === 'end' ? box.right - natural.width : box.left;
  const left = Math.min(
    Math.max(MARGIN, edge),
    Math.max(MARGIN, viewWidth - natural.width - MARGIN),
  );

  return { top, left, maxHeight: room };
}

function samePlacement(left: Placement | null, right: Placement): boolean {
  return (
    left !== null &&
    left.top === right.top &&
    left.left === right.left &&
    left.maxHeight === right.maxHeight
  );
}

/**
 * A surface in a portal, positioned against something on the page. It owns the
 * three ways a floating thing goes away — Escape, a click elsewhere, the anchor
 * scrolling out from under it — so nothing built on it has to own them again.
 */
export function Popover(props: PopoverProps): React.JSX.Element {
  const [surface, setSurface] = useState<HTMLDivElement | null>(null);
  const [placement, setPlacement] = useState<Placement | null>(null);

  const anchor = props.anchor;
  const align = props.align ?? 'start';
  const onClose = props.onClose;

  const reposition = useCallback(() => {
    if (surface === null) return;
    const next = place(anchor, { width: surface.offsetWidth, height: surface.scrollHeight }, align);
    setPlacement((current) => (samePlacement(current, next) ? current : next));
  }, [surface, anchor, align]);

  useLayoutEffect(reposition, [reposition]);

  useEffect(() => {
    if (surface === null) return undefined;

    function onPointerDown(event: PointerEvent): void {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (surface !== null && surface.contains(target)) return;
      // The trigger's own click toggles the surface; closing here as well
      // would shut it and reopen it in the same gesture.
      if (anchor.kind === 'element' && anchor.element.contains(target)) return;
      onClose('outside');
    }

    function onKeyDown(event: KeyboardEvent): void {
      if (event.key !== 'Escape') return;
      // Owned here, so the trigger underneath never sees it twice.
      event.stopPropagation();
      event.preventDefault();
      onClose('escape');
    }

    function onScroll(): void {
      if (anchor.kind === 'point') onClose('scroll');
      else reposition();
    }

    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', reposition);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', reposition);
    };
  }, [surface, anchor, onClose, reposition]);

  return createPortal(
    <div
      ref={setSurface}
      className={props.className === undefined ? 'popover' : `popover ${props.className}`}
      style={{
        top: placement?.top ?? 0,
        left: placement?.left ?? 0,
        maxHeight: placement?.maxHeight,
        // Measured before it is placed, so the first frame does not flash in
        // the corner of the window.
        visibility: placement === null ? 'hidden' : 'visible',
      }}
    >
      {props.children}
    </div>,
    document.body,
  );
}
